import React, { useState, useEffect } from 'react';
import { Calendar, Badge, Card, Select, Button, Modal, Typography, Space, Tag, Spin, message, Segmented } from 'antd';
import { PlusOutlined, CalendarOutlined, ScheduleOutlined, TeamOutlined } from '@ant-design/icons';
import { appointmentApi, Appointment } from '../../services/appointmentApi';
import { EnhancedAppointmentModal } from './EnhancedAppointmentModal';
import TechScheduleView from './TechScheduleView';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import axios from 'axios';
import { getTokens } from '../../services/authApi';

dayjs.extend(isBetween);

const { Title, Text } = Typography;

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

export const AppointmentCalendar: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedTechnician, setSelectedTechnician] = useState<number | 'all'>('all');
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'schedule'>('calendar');

  useEffect(() => {
    loadTechnicians();
    loadAppointments();
  }, []);

  useEffect(() => {
    filterAppointments();
  }, [appointments, selectedTechnician, selectedDate]);

  const loadTechnicians = async () => {
    try {
      // Get auth token
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      // Fetch both technicians and owners
      const [techniciansResponse, ownersResponse] = await Promise.all([
        axios.get('/api/users', {
          params: { role: 'technician', status: 'active' },
          headers
        }),
        axios.get('/api/users', {
          params: { role: 'owner', status: 'active' },
          headers
        })
      ]);

      // Combine and sort by name
      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadAppointments = async () => {
    setLoading(true);
    try {
      // Load appointments for current month
      const startOfMonth = selectedDate.startOf('month').format('YYYY-MM-DD');
      const endOfMonth = selectedDate.endOf('month').format('YYYY-MM-DD');

      const data = await appointmentApi.getAll({
        start_date: `${startOfMonth}T00:00:00`,
        end_date: `${endOfMonth}T23:59:59`
      });

      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      message.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const filterAppointments = () => {
    let filtered = [...appointments];

    // Filter by technician - now supports multi-tech assignments
    if (selectedTechnician !== 'all') {
      filtered = filtered.filter(apt => {
        // Check legacy technician_id field
        if (apt.technician_id === selectedTechnician) return true;

        // Check multi-tech assignments
        if (apt.technicians?.some(t => t.technician_id === selectedTechnician)) return true;

        return false;
      });
    }

    setFilteredAppointments(filtered);
  };

  const getAppointmentsForDate = (date: Dayjs): Appointment[] => {
    return filteredAppointments.filter(apt => {
      const aptDate = dayjs(apt.start_time);
      return aptDate.isSame(date, 'day');
    });
  };

  const dateCellRender = (value: Dayjs) => {
    const dayAppointments = getAppointmentsForDate(value);

    if (dayAppointments.length === 0) return null;

    return (
      <div style={{ fontSize: '12px' }}>
        {dayAppointments.slice(0, 3).map(apt => (
          <Badge
            key={apt.id}
            status={getAppointmentStatus(apt.status)}
            text={
              <div
                style={{
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAppointmentClick(apt);
                }}
              >
                {dayjs(apt.start_time).format('HH:mm')} - {apt.title}
              </div>
            }
          />
        ))}
        {dayAppointments.length > 3 && (
          <Text type="secondary" style={{ fontSize: '11px' }}>
            +{dayAppointments.length - 3} more
          </Text>
        )}
      </div>
    );
  };

  const getAppointmentStatus = (status: string): 'success' | 'processing' | 'default' | 'error' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'processing';
      case 'confirmed':
      case 'scheduled':
        return 'default';
      case 'cancelled':
        return 'error';
      case 'no_show':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setModalVisible(true);
  };

  const handleCreateAppointment = () => {
    setSelectedAppointment(null);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedAppointment(null);
    loadAppointments();
  };

  const handleMonthChange = (date: Dayjs) => {
    setSelectedDate(date);
    // Reload appointments for new month
    setTimeout(() => loadAppointments(), 0);
  };

  const getDayAppointmentsList = (date: Dayjs) => {
    const dayAppointments = getAppointmentsForDate(date);

    return dayAppointments.sort((a, b) =>
      dayjs(a.start_time).unix() - dayjs(b.start_time).unix()
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <Title level={2}>Appointments</Title>
          <Text type="secondary">View and manage all appointments with multi-tech support</Text>
        </div>
        <Space>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'calendar' | 'schedule')}
            options={[
              {
                label: 'Calendar',
                value: 'calendar',
                icon: <CalendarOutlined />
              },
              {
                label: 'Schedule',
                value: 'schedule',
                icon: <ScheduleOutlined />
              }
            ]}
          />
          <Select
            style={{ width: 250 }}
            value={selectedTechnician}
            onChange={setSelectedTechnician}
            placeholder="Filter by employee"
            showSearch
            filterOption={(input, option) => {
              const label = `${option?.label || ''}`.toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            options={[
              { value: 'all', label: 'All Employees' },
              ...technicians.map(tech => ({
                value: tech.id,
                label: `${tech.first_name} ${tech.last_name} (${tech.role === 'owner' ? 'Owner' : 'Technician'})`
              }))
            ]}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateAppointment}
          >
            New Appointment
          </Button>
        </Space>
      </div>

      {/* Conditional rendering based on view mode */}
      {viewMode === 'schedule' ? (
        <TechScheduleView />
      ) : (
        <Card>
          <Spin spinning={loading}>
            <Calendar
              dateCellRender={dateCellRender}
              onPanelChange={handleMonthChange}
              onSelect={(date) => setSelectedDate(date)}
            />
          </Spin>
        </Card>
      )}

      {/* Day View Panel - Only show in calendar mode */}
      {viewMode === 'calendar' && selectedDate && (
        <Card
          title={
            <Space>
              <CalendarOutlined />
              <span>Appointments for {selectedDate.format('MMMM DD, YYYY')}</span>
            </Space>
          }
          style={{ marginTop: '24px' }}
        >
          {getDayAppointmentsList(selectedDate).length === 0 ? (
            <Text type="secondary">No appointments scheduled for this day</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {getDayAppointmentsList(selectedDate).map(apt => {
                // Get multi-tech info
                const primaryTech = apt.technicians?.find(t => t.is_primary);
                const additionalTechs = apt.technicians?.filter(t => !t.is_primary) || [];
                const techCount = apt.technicians?.length || 1;

                return (
                  <Card
                    key={apt.id}
                    size="small"
                    hoverable
                    onClick={() => handleAppointmentClick(apt)}
                    style={{
                      borderLeft: `4px solid ${
                        apt.status === 'completed' ? '#52c41a' :
                        apt.status === 'in_progress' ? '#1890ff' :
                        apt.status === 'cancelled' ? '#ff4d4f' :
                        '#faad14'
                      }`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Text strong>{dayjs(apt.start_time).format('HH:mm')} - {dayjs(apt.end_time).format('HH:mm')}</Text>
                        <br />
                        <Text>{apt.title}</Text>
                        <br />
                        <Space size="small" wrap>
                          <Text type="secondary">
                            {primaryTech?.technician?.first_name || apt.technician?.first_name} {primaryTech?.technician?.last_name || apt.technician?.last_name}
                          </Text>
                          {additionalTechs.length > 0 && (
                            <Tag icon={<TeamOutlined />} color="purple">
                              +{additionalTechs.length} tech(s)
                            </Tag>
                          )}
                          {apt.customer && (
                            <Text type="secondary">| {apt.customer.first_name} {apt.customer.last_name}</Text>
                          )}
                        </Space>
                      </div>
                      <Space>
                        <Tag color={
                          apt.appointment_type === 'diagnosis' ? 'blue' :
                          apt.appointment_type === 'repair' ? 'green' :
                          apt.appointment_type === 'inspection' ? 'orange' : 'default'
                        }>
                          {apt.appointment_type.toUpperCase()}
                        </Tag>
                        <Tag color={
                          apt.status === 'completed' ? 'success' :
                          apt.status === 'in_progress' ? 'processing' :
                          apt.status === 'cancelled' ? 'error' : 'default'
                        }>
                          {apt.status.toUpperCase()}
                        </Tag>
                        {apt.has_conflict && !apt.conflict_acknowledged && (
                          <Tag color="red">CONFLICT</Tag>
                        )}
                        {apt.waiting_on_parts && (
                          <Tag color="warning">WAITING ON PARTS</Tag>
                        )}
                      </Space>
                    </div>
                  </Card>
                );
              })}
            </Space>
          )}
        </Card>
      )}

      {/* Enhanced Appointment Modal with multi-tech support */}
      <EnhancedAppointmentModal
        visible={modalVisible}
        appointment={selectedAppointment}
        onClose={handleModalClose}
      />
    </div>
  );
};
